import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Badge,
  Paper,
  Tooltip,
  Chip
} from '@mui/material';
import OverviewIcon from '@mui/icons-material/Dashboard';
import ActivityIcon from '@mui/icons-material/Timeline';
import TaskIcon from '@mui/icons-material/CheckCircleOutline';
import NotesIcon from '@mui/icons-material/Description';
import CallIcon from '@mui/icons-material/Phone';
import PropertyIcon from '@mui/icons-material/Business';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LeadOverview from './LeadOverview';
import LeadActivity from './LeadActivity';
import LeadTasks from './LeadTasks';
import LeadNotes from './LeadNotes';
import LeadCalls from './LeadCalls';
import LeadBookings from './LeadBookings';
import LeadPropertiesShared from './LeadPropertiesShared';
import LeadWhatsApp from './LeadWhatsApp';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';

const LeadTabs = ({ leadId, onCreateTask, openTaskModal, setOpenTaskModal, lead = null, onLeadUpdate, leadsDefaultFilters = null }) => {
  const { 
    activities = [], 
    activitiesPagination = {},
    notes = [], 
    calls = [], 
    callsPagination = {},
    tasks = [],
    tasksPagination = {},
    properties = [], 
    bookings = [],
    whatsappMessageCount = 0,
    loading = {},
    error = {},
  } = useLeadDetails();
  
  // Determine which tab to show based on bookings vs properties
  const hasBookings = bookings?.length > 0 || lead?.bookings?.length > 0;
  const hasProperties = properties?.length > 0 || lead?.properties?.length > 0;
 
  const [currentTab, setCurrentTab] = useState('overview');

  // Sync initial tab from URL hash (e.g. #overview, #notes)
  useEffect(() => {
    const raw = (window.location.hash || '').replace('#', '').trim();
    if (!raw) return;
    const lower = raw.toLowerCase();
    const allTabValues = [
      'overview',
      'notes',
      'tasks',
      'calls',
      'whatsapp',
      hasBookings ? 'bookings' : hasProperties ? 'properties' : null,
      'activity',
    ].filter(Boolean);
    if (allTabValues.includes(lower)) {
      setCurrentTab(lower);
    }
  }, [hasBookings, hasProperties]);

  const updateHashForTab = (tabValue) => {
    if (!tabValue) return;
    const targetHash = `#${tabValue}`;
    if (window.location.hash !== targetHash) {
      window.history.replaceState(null, '', targetHash);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    updateHashForTab(newValue);
  };
  
  const switchToTab = (tabName) => {
    setCurrentTab(tabName);
    updateHashForTab(tabName);
  };

  // Handle properties update to refresh lead data
  const handlePropertiesUpdate = () => {
    if (onLeadUpdate) {
      onLeadUpdate();
    }
  };



  // Tab definitions with icons, colors, and badges
  const tabDefinitions = [
    {
      value: 'overview',
      label: 'Overview',
      icon: <OverviewIcon sx={{ fontSize: 18 }} />,
      color: '#2196F3', // Blue
      count: null // No count for overview
    },
    {
      value: 'notes',
      label: 'Notes',
      icon: <NotesIcon sx={{ fontSize: 18 }} />,
      color: '#9C27B0', // Purple
      count: notes.length || 0
    },
    {
      value: 'tasks',
      label: 'Tasks',
      icon: <TaskIcon sx={{ fontSize: 18 }} />,
      color: '#FF9800', // Orange
      count: (tasksPagination.totalCount ?? tasks.length) || 0
    },
    {
      value: 'calls',
      label: 'Calls',
      icon: <CallIcon sx={{ fontSize: 18 }} />,
      color: '#00BCD4', // Cyan
      count: callsPagination.totalCount || calls.length || 0
    },
    {
      value: 'whatsapp',
      label: 'WhatsApp',
      icon: <WhatsAppIcon sx={{ fontSize: 18 }} />,
      color: '#25D366', // WhatsApp Green
      count: whatsappMessageCount
    }
  ];

  // Add conditional tab based on bookings vs properties
  if (hasBookings) {
    tabDefinitions.push({
      value: 'bookings',
      label: 'Bookings',
      icon: <PropertyIcon sx={{ fontSize: 18 }} />,
      color: '#E91E63', // Pink
      count: bookings?.length || lead?.bookings?.length || 0
    });
  } else {
    tabDefinitions.push({
      value: 'properties',
      label: 'Properties',
      icon: <PropertyIcon sx={{ fontSize: 18 }} />,
      color: '#795548', // Brown
      count: properties?.length || lead?.properties?.length || 0
    });
  }

  tabDefinitions.push({
    value: 'activity',
    label: 'Activity',
    icon: <ActivityIcon sx={{ fontSize: 18 }} />,
    color: '#4CAF50', // Green
    count: (activitiesPagination.totalCount ?? activities.length) || 0
  });
  
  return (
    <Box sx={{ 
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Tab header */}
      <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        zIndex: 1,
        flexShrink: 0,
        px: 3,
        py: 0,
        boxShadow: 'none',
        // Allow horizontal overflow for many tabs.
        overflowX: 'auto',
        // Slim horizontal scrollbar (match left sidebar style).
        '&::-webkit-scrollbar': {
          height: '3px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#E2E8F0',
          borderRadius: '2px',
        },
      }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="standard"
          TabIndicatorProps={{
            style: { display: 'none' }
          }}
          sx={{
            minHeight: 0,
            display: 'flex',
            width: 'max-content',
            px: 0,
            '.MuiTabs-flexContainer': {
              display: 'flex',
              width: 'max-content',
              gap: 0,
              justifyContent: 'flex-start',
              flexWrap: 'nowrap',
            }
          }}
        >
          {tabDefinitions.map((tab, idx) => (
            <Tab
              key={tab.value}
              value={tab.value}
              disableRipple
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center', width: '100%' }}>
                  <Box sx={{ color: tab.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {tab.icon}
                  </Box>
                  <Box component="span" sx={{ fontSize: '0.78rem', color: tab.value === currentTab ? tab.color : '#94A3B8' }}>
                    {tab.label}
                  </Box>
                  {tab.count !== null && (
                    <Chip
                      label={tab.count.toString()}
                      size="small"
                      sx={{
                        height: 18,
                        minWidth: 18,
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        backgroundColor: tab.value === currentTab ? tab.color : `${tab.color}15`,
                        color: tab.value === currentTab ? '#fff' : tab.color,
                        border: tab.value === currentTab ? 'none' : `1px solid ${tab.color}30`,
                        '& .MuiChip-label': {
                          px: 1,
                          py: 0
                        }
                      }}
                    />
                  )}
                </Box>
              }
              sx={{
                minHeight: 0,
                minWidth: 0,
                px: 0,
                mx: 0,
                borderRadius: 0,
                padding: '11px 14px',
                textTransform: 'none',
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: 'auto',
                width: 'auto',
                borderBottom: '2px solid transparent',
                marginBottom: '-1px',
                color: tab.value === currentTab ? tab.color : '#94A3B8',
                fontWeight: tab.value === currentTab ? 700 : 500,
                '&.Mui-selected': {
                  color: tab.color,
                  fontWeight: 700,
                  borderBottomColor: tab.color,
                  opacity: 1,
                },
                '&:hover': {
                  opacity: 1,
                  color: '#0F172A',
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab content - scrollable */}
      <Box 
        sx={{ 
          flex: '1 1 auto',
          overflow: 'auto',
          height: '100%',
          overflowX: 'auto',
          // Slim scrollbar for tab content (vertical + horizontal).
          '&::-webkit-scrollbar': {
            width: '4px',
            height: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#E2E8F0',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        }}
      >
        {/* Individual tab content */}
        {currentTab === 'overview' && 
          <LeadOverview
            leadId={leadId}
            onSwitchTab={switchToTab}
            onCreateTask={onCreateTask}
            openTaskModal={openTaskModal}
            setOpenTaskModal={setOpenTaskModal}
            lead={lead}
            leadsDefaultFilters={leadsDefaultFilters}
          />}
        {currentTab === 'activity' && 
          <LeadActivity
            leadId={leadId}
            activities={activities}
            loading={loading.activities}
            error={error.activities}
          />}
        {currentTab === 'calls' && 
          <LeadCalls leadId={leadId} calls={calls} lead={lead}/>}
        {currentTab === 'tasks' && 
          <LeadTasks leadId={leadId} onCreateTask={onCreateTask} lead={lead}/>}
        {currentTab === 'notes' && 
          <LeadNotes leadId={leadId} notes={notes} lead={lead}/>}
        {currentTab === 'bookings' && 
          <LeadBookings bookings={bookings || lead?.bookings || []} lead={lead}/>}
        {currentTab === 'properties' && 
          <LeadPropertiesShared properties={properties || lead?.properties || []} lead={lead} onPropertiesUpdate={handlePropertiesUpdate}/>}
        {currentTab === 'whatsapp' && 
          <LeadWhatsApp leadId={leadId} lead={lead}/>}
      </Box>
    </Box>
  );
};

export default LeadTabs;